import React, { useCallback } from 'react';
import PipelineAPI from '../../api/pipeline';
import { ClipMetadata } from '../../api/structs/clip';
import { useQualityReportDownload } from '../ideas/pipeline-run/qualityReportDownload';

const RUN_ID_KEYS = ['pipeline_run_id', 'source_run_id', 'run_id'] as const;

/** The run a clip was built from, read in the backend's own order
 *  (sceneAlternates.go): assembly stamps `pipeline_run_id`, older clips carry
 *  one of the other two. '' for a hand-built clip or an unhydrated stub. */
export const linkedRunId = (metadata: ClipMetadata | undefined): string => {
  for (const key of RUN_ID_KEYS) {
    const value = metadata?.[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

interface Props {
  clipId: string;
  metadata: ClipMetadata | undefined;
}

/** A deliberately quiet link: the clip's run evidence plus its own render,
 *  scene media, clip QA and reel review as one zip. Hidden when no run is
 *  linked, since the backend has nothing to export then. */
const ClipQualityExportAction: React.FC<Props> = ({ clipId, metadata }) => {
  const fetchArchive = useCallback(() => PipelineAPI.exportClipQualityReport(clipId), [clipId]);
  const { busy, error, download } = useQualityReportDownload(
    fetchArchive,
    `clip-${clipId.replace(/[^a-zA-Z0-9-]/g, '')}-quality.zip`
  );
  if (!linkedRunId(metadata)) return null;

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        data-testid="clip-export-quality-report"
        disabled={busy}
        onClick={() => void download()}
        title="Download this clip's run, final render, scene media and review evidence"
        className="text-[10px] font-mono uppercase tracking-widest text-[var(--app-text-muted)] hover:text-[var(--accent-strong)] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy ? 'Exporting…' : 'Export quality report'}
      </button>
      {error && <p role="alert" className="text-xs text-red-300">{error}</p>}
    </div>
  );
};

export default ClipQualityExportAction;
